import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCompanyCallStatus } from "./redux/slices/executorSlice";
import { fetchUserProfile } from "./redux/slices/sessionSlice";
import { getCookie, getAuthToken } from "./utils/cookieUtils";
import { ConfigProvider } from "antd";
import LandingPage from "./general/pages/landing-page/LandingPage";
import AdminDashboard from "./admin/dashboard/AdminDashboard";
import UserDashboard from "./user/dashboard/UserDashboard";
import SuperAdminDashboard from "./superadmin/dashboard/SuperAdminDashboard";
import PublicInvoiceView from "./general/components/invoice/PublicInvoiceView";
import SplashScreen from "./general/components/splash-screen/SplashScreen";
import "./App.css";

const isWindows = typeof navigator !== "undefined" && /Windows|Win32|Win64/i.test(navigator.userAgent || navigator.platform);

const appTheme = {
  token: {
    colorPrimary: "#2459d3",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: isWindows ? 13 : 14,
    controlHeight: isWindows ? 32 : 36,
    borderRadius: 8,
  },
};

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.session?.isAuthenticated);
  const companyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const userInfo = useSelector((state) => state.session?.settings?.userInfo);

  // Determine current role (super_admin vs company_admin vs employee / sales_rep)
  const role = userInfo?.role || getCookie("role") || "company_admin";
  const isSuperAdmin = role === "super_admin" || (role === "admin" && !companyCode);
  const isEmployee = role === "employee" || role === "sales_rep";

  const profileFetchedRef = useRef(false);

  useEffect(() => {
    const token = getAuthToken();
    if ((token || isAuthenticated) && !profileFetchedRef.current) {
      profileFetchedRef.current = true;
      dispatch(fetchCompanyCallStatus(companyCode));
      dispatch(fetchUserProfile(companyCode));
    }
  }, [dispatch, isAuthenticated, companyCode]);

  const getInvoiceTokenFromPath = () => {
    const path = window.location.pathname;
    if (path.toLowerCase().startsWith("/invoice/")) {
      return decodeURIComponent(path.slice(9).split("/")[0]);
    }
    if (path.toLowerCase().startsWith("/public/invoice/")) {
      return decodeURIComponent(path.slice(16).split("/")[0]);
    }
    return "";
  };

  const getInitialRoute = () => {
    const path = window.location.pathname.toLowerCase();
    if (path.startsWith("/invoice/") || path.startsWith("/public/invoice/")) {
      return "public-invoice";
    }
    const token = getAuthToken();
    if (path.startsWith("/dashboard") && token) {
      return "dashboard";
    }
    return "landing";
  };

  const [currentRoute, setCurrentRoute] = useState(getInitialRoute);

  const [isSplashing, setIsSplashing] = useState(() => {
    const token = getAuthToken();
    const path = window.location.pathname.toLowerCase();
    const isPublic = path.startsWith("/invoice/") || path.startsWith("/public/invoice/");
    return Boolean(token && (path.startsWith("/dashboard") || path === "/dashboard") && !isPublic);
  });
  const [isDashboardReady, setIsDashboardReady] = useState(false);
  const [splashUser, setSplashUser] = useState(null);

  useEffect(() => {
    const handlePopState = () => {
      const token = getAuthToken();
      const path = window.location.pathname.toLowerCase();
      if (path.startsWith("/invoice/") || path.startsWith("/public/invoice/")) {
        setCurrentRoute("public-invoice");
      } else if (path.startsWith("/dashboard") && !token) {
        window.history.replaceState({}, "", "/");
        setCurrentRoute("landing");
      } else {
        setCurrentRoute(getInitialRoute());
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // React immediately whenever authentication state changes (except for public invoice routes)
  useEffect(() => {
    const path = window.location.pathname.toLowerCase();
    if (path.startsWith("/invoice/") || path.startsWith("/public/invoice/")) {
      return;
    }
    const token = getAuthToken();
    if (isAuthenticated && token && currentRoute !== "dashboard") {
      setCurrentRoute("dashboard");
      window.history.pushState({}, "", "/dashboard");
    } else if ((!isAuthenticated || !token) && currentRoute === "dashboard") {
      setCurrentRoute("landing");
      setIsSplashing(false);
      setIsDashboardReady(false);
      window.history.replaceState({}, "", "/");
    }
  }, [isAuthenticated, currentRoute]);

  const navigateTo = (route) => {
    const token = getAuthToken();
    if (route === "dashboard" && !token) {
      // Block navigation to dashboard if no token in cookies
      setCurrentRoute("landing");
      setIsSplashing(false);
      setIsDashboardReady(false);
      window.history.replaceState({}, "", "/");
      return;
    }

    setCurrentRoute(route);
    const newPath = route === "dashboard" ? "/dashboard" : "/";
    window.history.pushState({}, "", newPath);
  };

  const handleLoginSuccess = (userObj) => {
    setSplashUser(userObj || userInfo);
    setIsDashboardReady(false);
    setIsSplashing(true);
    dispatch(fetchUserProfile(userObj?.companyCode));
    navigateTo("dashboard");
  };

  const handleDashboardReady = useCallback(() => {
    setIsDashboardReady(true);
  }, []);

  const handleLogout = () => {
    setIsSplashing(false);
    setIsDashboardReady(false);
    setSplashUser(null);
    navigateTo("landing");
  };

  // Public unauthenticated routes
  if (currentRoute === "public-invoice" || window.location.pathname.toLowerCase().startsWith("/invoice/") || window.location.pathname.toLowerCase().startsWith("/public/invoice/")) {
    return (
      <ConfigProvider theme={appTheme}>
        <PublicInvoiceView token={getInvoiceTokenFromPath()} />
      </ConfigProvider>
    );
  }

  // Protected Route Rendering: Dynamically render SuperAdminDashboard vs UserDashboard vs AdminDashboard by role
  if ((isAuthenticated || Boolean(getAuthToken())) && currentRoute === "dashboard") {
    let dashboardContent = <AdminDashboard onLogout={handleLogout} onReady={handleDashboardReady} />;
    if (isSuperAdmin) {
      dashboardContent = <SuperAdminDashboard onLogout={handleLogout} onReady={handleDashboardReady} />;
    } else if (isEmployee) {
      dashboardContent = <UserDashboard onLogout={handleLogout} onReady={handleDashboardReady} />;
    }

    return (
      <ConfigProvider theme={appTheme}>
        {dashboardContent}
        {isSplashing && <SplashScreen user={splashUser || userInfo} companyName={companyCode} isReady={isDashboardReady} onFinish={() => setIsSplashing(false)} minDurationMs={1200} />}
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={appTheme}>
      <LandingPage onLoginSuccess={handleLoginSuccess} />
      {isSplashing && <SplashScreen user={splashUser || userInfo} companyName={companyCode} isReady={isDashboardReady} onFinish={() => setIsSplashing(false)} minDurationMs={1200} />}
    </ConfigProvider>
  );
}

export default App;
